'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useActiveModel, type ModelConfidence } from '../lib/models';
import { Chat } from './Chat';
import { ReportTemplates } from './ReportTemplates';
import { getDefaultTemplate, type AddToReportSeed, type ReportTemplate } from '../lib/templates';
import { DashboardView } from './DashboardView';
import { PageLoader } from './PageLoader';
import type { ModelRef } from '../lib/api';

/**
 * Dashboard (integration week). Loads the active model from the API — generating
 * one from the bundled spec on first run — then renders the KPI tiles and chat
 * against that pipeline-produced model, closing the build → store → serve → query
 * → dashboard loop. A model bar shows provenance (generated vs. offline).
 */
export function Dashboard(): React.JSX.Element {
  const { active, loading, error, regenerate } = useActiveModel();
  // The tenant's landing dashboard. `undefined` = still loading, `null` = none set:
  // the Dashboard tab only exists once a template claims the spot, so a tenant that
  // has never built one still opens on Ask.
  const [dashboard, setDashboard] = useState<ReportTemplate | null | undefined>(undefined);
  const [view, setView] = useState<'dashboard' | 'ask' | 'reports'>('ask');
  const loadDefault = useCallback(async () => {
    const t = await getDefaultTemplate().catch(() => null);
    setDashboard(t);
    return t;
  }, []);
  // On boot, land on the dashboard when there is one. Only on boot — `loadDefault`
  // is also called after the default moves, and switching tabs then would yank
  // someone off the list they are still working in.
  //
  // reactStrictMode double-invokes mount effects in dev; the ref persists across
  // that simulated remount so the default-template GET fires once, not twice. It
  // resets on a genuine remount, so a fresh Dashboard still loads on boot.
  const didLoadDefault = useRef(false);
  useEffect(() => {
    if (didLoadDefault.current) return;
    didLoadDefault.current = true;
    void loadDefault().then((t) => {
      if (t) setView('dashboard');
    });
  }, [loadDefault]);
  const [seed, setSeed] = useState<AddToReportSeed | null>(null);
  // What the query API is told about the model. A 'generated' model is the
  // tenant's stored ACTIVE one, so the server loads it itself and the request
  // carries nothing — the model goes out once via /models/active instead of
  // riding along with every question. Only the offline fallback, which the
  // server has never seen, still travels inline.
  //
  // Memoised on `active`: a fresh object every render would change the identity
  // the suggestions effect depends on and refetch on each one.
  const modelRef = useMemo<ModelRef>(
    () => (active?.source === 'offline' ? { model: active.model } : {}),
    [active],
  );

  if (loading && !active) {
    return (
      <main style={mainStyle}>
        <p className="muted">Loading model…</p>
      </main>
    );
  }
  if (!active) {
    return (
      <main style={mainStyle}>
        <p className="muted">No model available. {error}</p>
      </main>
    );
  }

  return (
    <main style={mainStyle}>
      <ModelBar
        name={active.name}
        source={active.source}
        entityCount={active.entityCount}
        confidence={active.confidence}
        riskCount={active.riskCount}
        onRegenerate={regenerate}
        note={error}
      />

      {/* The tab strip waits too: whether Dashboard exists is part of the same
          unanswered question, so showing the strip early would render it without
          that tab and then shift the others sideways when it appears. */}
      {dashboard === undefined ? null : (
        <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border)' }}>
          {dashboard ? <TabButton label="Dashboard" active={view === 'dashboard'} onClick={() => setView('dashboard')} /> : null}
          <TabButton label="Ask" active={view === 'ask'} onClick={() => setView('ask')} />
          <TabButton label="Report templates" active={view === 'reports'} onClick={() => setView('reports')} />
        </div>
      )}

      {dashboard === undefined ? (
        // Which tab opens depends on whether a default dashboard exists, and that
        // answer is one request away. Rendering Ask in the meantime would flash a
        // tab the user never chose, so hold the frame until the answer lands.
        <PageLoader label="Loading your dashboard…" />
      ) : view === 'dashboard' && dashboard ? (
        <DashboardView
          template={dashboard}
          modelRef={modelRef}
          onBack={() => setView('reports')}
        />
      ) : view === 'ask' ? (
        <section>
          <Chat
            model={active.model}
            modelRef={modelRef}
            onAddToTemplate={(report, templateId) => {
              setSeed({ report, templateId });
              setView('reports');
            }}
          />
        </section>
      ) : (
        <ReportTemplates
          modelRef={modelRef}
          seed={seed}
          onSeedConsumed={() => setSeed(null)}
          onDefaultChanged={() => void loadDefault()}
        />
      )}
    </main>
  );
}

function TabButton({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '8px 14px',
        fontSize: 14,
        fontWeight: 600,
        border: 'none',
        borderBottom: `2px solid ${active ? 'var(--accent, #4f46e5)' : 'transparent'}`,
        background: 'transparent',
        color: active ? 'var(--text)' : 'var(--text-muted)',
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}

const CONFIDENCE_COLOR: Record<'High' | 'Medium' | 'Low', string> = {
  High: 'var(--good)',
  Medium: 'var(--accent)',
  Low: 'var(--bad)',
};

function ModelBar({
  name,
  source,
  entityCount,
  confidence,
  riskCount,
  onRegenerate,
  note,
}: {
  name: string;
  source: 'generated' | 'offline';
  entityCount: number;
  confidence: ModelConfidence | null;
  riskCount: number;
  onRegenerate: () => void;
  note: string | null;
}): React.JSX.Element {
  const pill = {
    marginLeft: 8,
    fontSize: 12,
    padding: '2px 8px',
    borderRadius: 999,
    border: '1px solid var(--border)',
  } as const;
  return (
    <div
      className="card"
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: 14 }}
    >
      <div style={{ fontSize: 14 }}>
        <strong>{name}</strong>{' '}
        <span className="muted">· {entityCount} entities</span>{' '}
        <span
          style={{
            marginLeft: 8,
            fontSize: 12,
            padding: '2px 8px',
            borderRadius: 999,
            border: '1px solid var(--border)',
            color: source === 'generated' ? 'var(--good)' : 'var(--text-muted)',
          }}
        >
          {source === 'generated' ? 'pipeline-generated' : 'offline demo'}
        </span>
        {confidence ? (
          <span
            title="Model confidence — how trustworthy this generated model is (data completeness, governance, relationship integrity)."
            style={{ ...pill, color: CONFIDENCE_COLOR[confidence.band] }}
          >
            confidence {confidence.score} · {confidence.band}
          </span>
        ) : null}
        {riskCount > 0 ? (
          <span style={{ ...pill, color: 'var(--bad)' }}>
            {riskCount} risk{riskCount === 1 ? '' : 's'}
          </span>
        ) : null}
        {note && source === 'offline' ? (
          <span className="muted" style={{ marginLeft: 8, fontSize: 12 }}>
            (API unreachable)
          </span>
        ) : null}
      </div>
      <button
        type="button"
        onClick={onRegenerate}
        style={{
          padding: '6px 12px',
          fontSize: 13,
          borderRadius: 6,
          border: '1px solid var(--border)',
          background: 'transparent',
          color: 'var(--text-secondary)',
          cursor: 'pointer',
        }}
      >
        Regenerate
      </button>
    </div>
  );
}

const mainStyle: React.CSSProperties = {
  maxWidth: 900,
  margin: '0 auto',
  padding: '28px 24px 64px',
  display: 'grid',
  gap: 24,
};
